import React, { useEffect, useState } from 'react';
import { api } from '../../middleware/api';
import { END_POINTS } from '../../utils/constants/endPoints';
import InterviewSidebar from './InterviewSidebar';
import { styles } from './interviewStyles';

const PACK_SIZE = 10;
const PACK_PRICE = 11.95;

const InterviewBilling: React.FC = () => {
  const [totalCredits, setTotalCredits] = useState(0);
  const [used, setUsed] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [purchased, setPurchased] = useState(false);

  useEffect(() => {
    // Check for ?purchased=1 query param
    const params = new URLSearchParams(window.location.search);
    if (params.get('purchased') === '1') {
      setPurchased(true);
      // Clean up the URL
      window.history.replaceState({}, '', window.location.pathname);
    }

    (async () => {
      try {
        const r = await api.get(END_POINTS.interviewCredits, {});
        setTotalCredits(r?.totalCredits ?? 0);
        setUsed(r?.used ?? 0);
        setRemaining(r?.remaining ?? 0);
      } catch (e: any) {
        console.error('Credits load error:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const r = await api.post(END_POINTS.interviewPackCheckout, { quantity });
      if (r?.data?.checkoutUrl) {
        window.location.href = r.data.checkoutUrl;
      } else {
        alert('Could not create checkout session. Please try again.');
        setPurchasing(false);
      }
    } catch (e: any) {
      console.error('Checkout error:', e);
      alert('Error creating checkout. Please try again.');
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <InterviewSidebar>
        <div style={styles.emptyState}>
          <div className="spinner-border" role="status" style={{ color: '#0065FF' }}>
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '20px', ...styles.secondaryText }}>Loading billing...</p>
        </div>
      </InterviewSidebar>
    );
  }

  const isLocked = remaining <= 0;
  const progressPct = totalCredits > 0 ? Math.min(100, (used / totalCredits) * 100) : 0;
  const totalPrice = (quantity * PACK_PRICE).toFixed(2);
  const totalInterviews = quantity * PACK_SIZE;

  return (
    <InterviewSidebar>
      {/* Purchase Success Banner */}
      {purchased && (
        <div style={{
          ...styles.card,
          marginBottom: '24px',
          backgroundColor: '#D4EDDA',
          border: '1px solid #C3E6CB',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#155724" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <div>
              <strong style={{ color: '#155724', fontSize: '16px' }}>Purchase successful!</strong>
              <p style={{ color: '#155724', margin: '4px 0 0', fontSize: '14px' }}>
                Your interview credits have been added to your account.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Lockout Banner */}
      {isLocked && (
        <div style={{
          ...styles.card,
          marginBottom: '24px',
          backgroundColor: '#FFF3CD',
          border: '1px solid #FFEEBA',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#856404" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <div>
              <strong style={{ color: '#856404', fontSize: '16px' }}>No interviews remaining</strong>
              <p style={{ color: '#856404', margin: '4px 0 0', fontSize: '14px' }}>
                Purchase an interview pack below to continue interviewing candidates.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Usage Card */}
      <div style={{ ...styles.card, marginBottom: '24px' }}>
        <h3 style={styles.cardTitle}>Interview Usage</h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '14px', color: '#555555' }}>
            {used} of {totalCredits} interviews used
          </span>
          <span style={{
            ...styles.badge,
            backgroundColor: isLocked ? '#dc3545' : '#28a745',
            color: '#ffffff',
            fontSize: '12px',
            padding: '4px 12px',
          }}>
            {isLocked ? 'No credits' : `${remaining} remaining`}
          </span>
        </div>
        <div style={{
          width: '100%',
          height: '10px',
          backgroundColor: '#E9ECEF',
          borderRadius: '5px',
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${progressPct}%`,
            height: '100%',
            backgroundColor: isLocked ? '#dc3545' : '#0065FF',
            borderRadius: '5px',
            transition: 'width 0.4s ease',
          }} />
        </div>
      </div>

      {/* Pricing & Purchase Card */}
      <div style={{ ...styles.card, marginBottom: '24px' }}>
        <h3 style={styles.cardTitle}>Purchase Interview Packs</h3>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
          <span style={{ fontSize: '42px', fontWeight: 'bold', color: '#141414' }}>${PACK_PRICE.toFixed(2)}</span>
          <span style={{ fontSize: '16px', color: '#555555' }}>+ GST per pack</span>
        </div>
        <p style={{ fontSize: '18px', color: '#555555', marginBottom: '24px', fontWeight: 500 }}>
          {PACK_SIZE} interviews per pack
        </p>

        <div style={{ marginBottom: '24px' }}>
          {[
            'AI-powered realistic interviews',
            'Detailed candidate performance reports',
            'Resume analysis & tailored questions',
            'Performance ratings & recommendations',
            'Strengths & areas for improvement breakdown',
          ].map((feature, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span style={{ fontSize: '14px', color: '#333' }}>{feature}</span>
            </div>
          ))}
        </div>

        {/* Quantity Selector */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: '8px' }}>
            Number of packs
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <select
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              style={{
                padding: '10px 16px',
                fontSize: '16px',
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                backgroundColor: '#fff',
                cursor: 'pointer',
                minWidth: '80px',
              }}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span style={{ fontSize: '16px', color: '#555' }}>
              = <strong>{totalInterviews} interviews</strong> for <strong>${totalPrice} + GST</strong>
            </span>
          </div>
        </div>

        <button
          style={{
            ...styles.button,
            ...styles.primaryButton,
            width: '100%',
            padding: '14px 24px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: purchasing ? 'wait' : 'pointer',
            opacity: purchasing ? 0.7 : 1,
          }}
          onClick={handlePurchase}
          disabled={purchasing}
        >
          {purchasing
            ? 'Redirecting to checkout...'
            : `Purchase ${totalInterviews} Interviews — $${totalPrice} + GST`
          }
        </button>
      </div>

      {/* Info Card */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>How it works</h3>
        <p style={{ ...styles.secondaryText, lineHeight: 1.6 }}>
          Purchase interview packs of {PACK_SIZE} for just ${PACK_PRICE.toFixed(2)} + GST to get started.
          Each interview includes a full AI-powered session with detailed candidate reports,
          ratings, and actionable feedback. Credits never expire.
        </p>
      </div>
    </InterviewSidebar>
  );
};

export default InterviewBilling;
